import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import BlackHole from "@/studio/components/BlackHole";
import ThemeToggle from "@/studio/components/ThemeToggle";
import { scrollToId } from "@/studio/lib/useStudioScroll";
import { useTheme } from "@/studio/lib/theme";

const LINKS = [
  { label: "Work", id: "work" },
  { label: "Services", id: "services" },
  { label: "Tech", id: "tech" },
  { label: "Architect", id: "architect" },
  { label: "Studio", id: "founder" },
];

const ease = [0.16, 1, 0.3, 1];

export default function Nav({ onContact, ready = true }) {
  const { theme } = useTheme();
  const [hidden, setHidden] = useState(false);
  const [overDark, setOverDark] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let last = window.scrollY;
    let raf;
    const navY = 34;
    const compute = () => {
      const y = window.scrollY;
      setHidden(y > 240 && y > last + 2);
      last = y;
      const els = document.querySelectorAll("[data-nav-dark]");
      let isDark = false;
      els.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top <= navY && r.bottom > navY) isDark = true;
      });
      setOverDark(isDark);
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // close the mobile menu on Escape + lock body scroll while open
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    if (window.__lenis) window.__lenis.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (window.__lenis) window.__lenis.start();
    };
  }, [menuOpen]);

  // Nav reads as "dark" when over a dark section OR when global theme is dark
  const dark = overDark || theme === "dark";
  const textColor = dark ? "text-white" : "text-[#0a0a0a]";
  const subColor = dark ? "text-white/85" : "text-[#0a0a0a]/80";

  const go = (id) => {
    setMenuOpen(false);
    // wait for the overlay to release scroll lock before smooth-scrolling
    setTimeout(() => scrollToId(id), 60);
  };

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: !ready ? -40 : hidden && !menuOpen ? -120 : 0, opacity: ready ? 1 : 0 }}
        transition={{ duration: 0.7, ease, delay: ready ? 0.25 : 0 }}
        className="fixed inset-x-0 top-0 z-[120]"
      >
        <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-3 px-4 py-3.5 sm:px-5 sm:py-4 md:px-10 md:py-5">
          {/* left — black hole icon + wordmark (static, premium) */}
          <button
            onClick={() => go("top")}
            data-cursor="big"
            aria-label="Parabolum — back to top"
            className="group flex min-w-0 items-center gap-2.5 opacity-100 transition-opacity duration-300 hover:opacity-80"
          >
            <span className="relative inline-flex h-7 w-7 shrink-0 items-center justify-center">
              <BlackHole className="h-full w-full" parallax={0} idle={false} />
            </span>
            <span
              className={`ff-display whitespace-nowrap text-[16px] font-extrabold tracking-tight transition-colors duration-500 sm:text-[18px] ${textColor}`}
            >
              PARABOLUM<span className="text-[#E4FF00]">.</span>
            </span>
          </button>

          {/* right — DESKTOP: links + theme + cta */}
          <div className="hidden items-center gap-6 md:flex md:gap-9">
            <nav className="flex items-center gap-9">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => scrollToId(l.id)}
                  className={`link-underline text-[13px] font-medium tracking-wide transition-colors duration-500 ${subColor}`}
                >
                  {l.label}
                </button>
              ))}
            </nav>

            <ThemeToggle />

            <button
              onClick={onContact}
              data-cursor="big"
              className={`flex items-center gap-2 rounded-full border px-4 py-2 text-[13px] font-semibold transition-colors duration-500 ${
                dark
                  ? "border-white/40 text-white hover:bg-white hover:text-black"
                  : "border-[#0a0a0a]/25 text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white"
              }`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#E4FF00]" />
              Let&apos;s talk
            </button>
          </div>

          {/* right — MOBILE: compact theme toggle + menu button only */}
          <div className="flex shrink-0 items-center gap-2.5 md:hidden">
            <ThemeToggle compact />
            <button
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-button"
              className={`inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${
                dark ? "border-white/25 text-white" : "border-[#0a0a0a]/20 text-[#0a0a0a]"
              }`}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </motion.header>

      {/* MOBILE MENU OVERLAY */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="fixed inset-0 z-[200] flex flex-col bg-[#030303] text-white md:hidden"
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease }}
          >
            <div className="flex items-center justify-between px-4 py-3.5 sm:px-5 sm:py-4">
              <span className="ff-display text-[16px] font-extrabold tracking-tight sm:text-[18px]">
                PARABOLUM<span className="text-[#E4FF00]">.</span>
              </span>
              <button
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="flex flex-1 flex-col justify-center gap-1 px-5 pb-8">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  onClick={() => go(l.id)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.08 + i * 0.05 }}
                  className="ff-display flex w-full items-baseline gap-4 border-b border-white/10 py-5 text-left text-[34px] font-extrabold leading-none tracking-tight"
                >
                  <span className="text-[12px] font-medium text-white/30">0{i + 1}</span>
                  {l.label}
                </motion.button>
              ))}
            </nav>

            <div className="px-5 pb-[max(28px,env(safe-area-inset-bottom))]">
              <button
                onClick={() => {
                  setMenuOpen(false);
                  setTimeout(() => onContact?.(), 60);
                }}
                data-testid="mobile-menu-cta"
                className="flex w-full items-center justify-center gap-2.5 rounded-full bg-[#E4FF00] px-6 py-4 text-[15px] font-semibold text-black"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-black" />
                Let&apos;s talk
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
